<template>
    <vx-table-row>
        <vx-table-row-title>
            {{ model.email }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Group Role: </template>
            {{ GROUP_USER_ROLES[model.role] || '-' }}
        </vx-table-row-item>

         <vx-table-row-item>
            <template v-slot:label> Status: </template>
            {{ model.status === 'pending' ? 'Pending' : 'Expired' }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Invited At: </template>
            <span :class="invitedAtColor" :title="shortDateTime(model.updated_at)" :key="invitedAtKey">
                {{ fromNow(model.updated_at) }}
            </span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="onCorporate() || ['admin'].includes(roleOnCurrentGroup)">
            <vx-button
                data-test="delete"
                @click="deleteInvite()"
                type="button"
                color="muted"
                outline
                class="w-[45px]"
            >
                <span class="hidden">Cancel Invite</span>
                <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
            </vx-button>

            <vx-button data-test="resend" @click="resendInvite()" color="muted" outline>
                Re-send <font-awesome-icon widthAuto :icon="faPaperPlane" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faPaperPlane, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed } from 'vue';
import { GROUP_USER_ROLES } from '~/constants/roles';
import dayjs from '~/utils/dayjs';
import { useInterval } from '@vueuse/core';
import { fromNow, shortDateTime } from '~/utils/date';
import { groupInvitationsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const model = defineModel({
    type: Object,
    required: true,
});

const emit = defineEmits(['deleted', 'resent']);

const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();

const deleteInvite = (emitDeleted = true) => {
    return VxSwal.confirmation('Cancel Invite', 'Are you sure? There is no undo.', 'danger', () =>
        groupInvitationsClient
            .deleteInvitation(props.teamId, props.groupId, model.value.id)
            .then(() => {
                if (emitDeleted) {
                    emit('deleted', model.value.id);
                    toasted.success(`${model.value.email} invite canceled`);
                }
            })
            .catch(throwSwalRequestError)
    );
};

const resendInvite = async () => {
    return VxSwal.confirmation(
        'Re-send Invite',
        'Are you sure you want to re-send the invite?',
        'success',
        () =>
            groupInvitationsClient
                .resendInvitation(props.teamId, props.groupId, model.value.id)
                .then((response) => {
                    model.value = response.data;
                    emit('resent', model.value);
                    toasted.success('Invitation re-sent successfully');
                })
                .catch(throwSwalRequestError)
    );
};

const invitedAtKey = useInterval(60000);
const invitedAtColor = computed(() => {
    if (model.value.status !== 'pending') {
        return 'text-rose-600';
    }

    const diffInDays = dayjs().diff(model.value.updated_at, 'days');

    if (diffInDays > 5) {
        return 'text-amber-600';
    }

    return 'text-emerald-700';
});
</script>
